export const en = {
  meta: {
    title: 'AuthFlow™ - Simple Email/Password Authentication with JWT Tokens',
    description: 'Reliable email/password authentication with JWT tokens for modern web applications. Unlike complex platforms, we focus on what works: secure, simple login that developers can trust.',
  },
  // Navigation
  nav: {
    home: 'Home',
    features: 'Features',
    pricing: 'Pricing',
    compare: 'Compare',
    contact: 'Contact',
    getStarted: 'Get Started',
    signIn: 'Sign In',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    users: 'Users',
    activity: 'Activity',
    settings: 'Settings',
    logout: 'Log out',
    profileSettings: 'Profile Settings',
    activityLog: 'Activity Log',
    welcome: 'Welcome to your dashboard',
    welcomeBack: 'Welcome back, {name}. Here\'s what\'s happening with your authentication service.',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    totalLogins: 'Total Logins',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No recent activity',
    userSettings: 'User settings',
    organizationOwnerCannotBeManaged: 'Integration Owner cannot be managed',
    configureSettings: 'Configure Settings',
    hereIsWhatsHappening: 'Here\'s what\'s happening with your authentication service.',
    fromLastMonth: 'from last month',
    monthlyLogins: 'Monthly Logins',
    successRate: 'Success Rate',
    latestAuthEvents: 'Latest authentication events and system updates',
    viewAllActivity: 'View All Activity',
    userManagement: 'User Management',
    manageApplicationUsers: 'Manage your application users',
    viewAllUsers: 'View All Users',
    apiKeys: 'API Keys',
    manageApiKeys: 'Manage your authentication API keys',
    systemStatus: 'System Status',
    authenticationApi: 'Authentication API',
    operational: 'Operational',
    userManagementService: 'User Management',
    searchUsers: 'Search users...',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    lastLogin: 'Last login',
    never: 'Never',
    admin: 'Admin',
    owner: 'Owner',
    noUsersFound: 'No users found matching your criteria',
    noUsers: 'No users found',
    for: 'for',
    noApiKeyFound: 'No API key found',
    hideApiKey: 'Hide API key',
    showApiKey: 'Show API key',
    created: 'Created',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    system: 'System',
    justNow: 'just now',
    minutesAgo: 'minutes ago',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',
    notifications: 'Notifications',
    billingReminder: 'Billing Reminder',
    noNewNotifications: 'No new notifications',
    logOut: 'Log out',
  },

  // Hero Section
  hero: {
    badge: 'Trusted by DevSecOps Worldwide',
    title: 'Authentication',
    subtitle: 'Done Right',
    description: 'Best, fastest, most secure email-and-password authentication available. Unlike overly-complex authentication platforms, we focus on what matters: secure JWT authentication that just works.',
    startTrial: 'Start Free Trial',
    getStarted: 'Get Started',
    trialNote: 'No credit card required • 10 Monthly Active Users Free',
    metrics: {
      firstIntegrationValue: '5 min',
      firstIntegrationLabel: 'to first integration',
      predictableSessionsValue: 'JWT',
      predictableSessionsLabel: 'sessions that stay predictable',
      noMazeValue: 'Zero maze',
      noMazeLabel: 'for teams that just need auth to work',
    },
    missionControlLabel: 'Auth mission control',
    missionControlTitle: 'Authenticating Users, minus maze',
    missionControlStatus: 'Stable',
    sessionLayer: 'Session layer',
    emailPasswordSignIn: 'Email/password sign-in',
    roleAwareVerification: 'Role-aware verification',
    validated: 'validated',
    live: 'live',
    integrationShapeLabel: 'Integration shape',
    integrationShapeValue: 'Small API, clear flow',
    forTeamsLabel: 'For teams',
    forTeamsValue: 'Shipping product, not IAM projects',
  },

  // Features
  features: {
    title: 'Why AuthFlow™ is different',
    description: 'We focus on email/password authentication with JWT tokens - the foundation of secure web apps. Unlike platforms that try to do everything, we do one thing exceptionally well.',
    emailPassword: {
      title: 'Email/Password Login',
      description: 'Simple, secure email and password authentication that works reliably. No complex flows or unnecessary features - just login that your users understand.',
    },
    jwtTokens: {
      title: 'JWT Token Management',
      description: 'Proper JWT token handling for sessions, with secure storage and automatic refresh. Industry-standard tokens that integrate seamlessly with your API.',
    },
    developerApi: {
      title: 'Developer-First API',
      description: 'RESTful API with comprehensive documentation, SDKs, and developer tools. Built by developers for the DevSecOps workflow.',
    },
  },

  // Testimonials
  testimonials: {
    title: 'Trusted by developers worldwide',
    testimonial1: {
      text: 'AuthFlow™ saved us months of development time. The integration was seamless and the security features are enterprise-grade.',
      author: 'Sarah Johnson',
      role: 'CTO, TechStart',
    },
    testimonial2: {
      text: 'The analytics dashboard gives us incredible insights into user behavior. Best auth solution we\'ve used.',
      author: 'Mike Rodriguez',
      role: 'Lead Developer, AppCorp',
    },
    testimonial3: {
      text: 'Rock-solid reliability and excellent support. Our users love the smooth authentication experience.',
      author: 'Alex Liu',
      role: 'Product Manager, InnovateLab',
    },
  },

  // CTA Section
  cta: {
    title: 'Ready to add reliable authentication?',
    description: 'Join developers who choose simple, effective email/password authentication with JWT tokens. Start your free trial today.',
    startTrial: 'Start Free Trial',
  },

  // About Page
  about: {
    title: 'About AuthFlow™ - Simple Email/Password Authentication Experts',
    description: 'Learn about AuthFlow™\'s mission to provide reliable email/password authentication with JWT tokens. Founded in 2023, we make secure login simple for every developer.',
    keywords: [
      'email password authentication',
      'JWT authentication company',
      'authentication experts',
      'simple authentication',
      'secure login company',
      'developer authentication',
      'reliable authentication',
      'token-based authentication',
      'web app security',
      'authentication platform'
    ],
    ogTitle: 'About AuthFlow™ - Simple Email/Password Authentication Experts',
    ogDescription: 'Learn about our mission to provide reliable email/password authentication with JWT tokens. Unlike complex platforms, we focus on what works.',
    hero: {
      badge: 'Founded in 2023',
      title: 'Building the Future of',
      titleAccent: 'Simple Authentication',
      description: 'We\'re on a mission to make email/password authentication with JWT tokens accessible to every developer, eliminating the complexity and security risks of building auth from scratch.',
    },
    mission: {
      title: 'Our Mission',
      description1: 'To democratize secure, token-based authentication that is simple to implement and maintain. We believe every application deserves simple, reliable email/password authentication with JWT tokens, without the complexity-headaches of over-engineered platforms.',
      description2: 'We prioritize privacy and compliance by design—minimizing data collection, encrypting sensitive information at rest and in transit, and offering configurable retention policies. Our aim is to help teams meet regulatory requirements without sacrificing usability or developer velocity.',
      description3: 'By abstracting away the intricacies of over-engineered security protocols, security-by-obscurity, and user management, we enable developers to focus on what they do best: building amazing products with reliable authentication.',
    },
    vision: {
      title: 'Our Vision',
      description1: 'A world where authentication is simple and reliable. We envision a future where every web application has access to trustworthy email/password authentication with JWT tokens, without the complexity and unreliability of over-engineered platforms.',
      description2: 'Through continuous innovation and community-driven development, we\'re building the authentication infrastructure that focuses on what matters: secure, simple login that developers can depend on.',
    },
    values: {
      title: 'Our Values',
      subtitle: 'The principles that guide everything we do at AuthFlow™.',
      reliableSecurity: {
        title: 'Reliable Security',
        description: 'We focus on battle-tested security practices that work. Email/password authentication with proper JWT token management - no unnecessary complexity.',
      },
      developerFirst: {
        title: 'Developer-First API',
        description: 'We obsess over making our tool intuitive, well-documented, and delightful to use. Great developer experience leads to better developer-designed products with secure authentication.',
      },
      communityDriven: {
        title: 'Community Driven',
        description: 'Our roadmap is shaped by you the developer-customer. We listen, learn, and build based on real-world security needs.',
      },
    },
    team: {
      title: 'Meet Our Team',
      subtitle: 'The passionate individuals building the future of authentication.',
      alex: {
        initials: 'AJ',
        name: 'Alex Johnson',
        role: 'CEO & Co-Founder',
        bio: 'Former DevSecOps at major U.S. multinational financial institution. Passionate about making reliable email/password authentication accessible to all developers.',
      },
      sarah: {
        initials: 'SC',
        name: 'Sarah Chen',
        role: 'CTO & Co-Founder',
        bio: 'Full-stack architect with 10+ years building reliable authentication systems. Expert in email/password authentication and JWT token management.',
      },
      marcus: {
        initials: 'MR',
        name: 'Marcus Rodriguez',
        role: 'Head of Product',
        bio: 'Product leader focused on developer experience and reliable authentication. Expert in simple, effective email/password login systems.',
      },
    },
    stats: {
      title: 'AuthFlow™ by the Numbers',
      developers: 'Developers Trust Us',
      authentications: 'Monthly Authentications',
      uptime: 'Uptime',
      monitoring: 'Security Monitoring',
    },
    cta: {
      title: 'Join Our Mission',
      description: 'Ready to be part of the authentication revolution? Start building with reliable email/password authentication today.',
      getStarted: 'Get Started Free',
      contactUs: 'Contact Us',
    },
    breadcrumb: {
      company: 'Company',
      aboutUs: 'About Us',
    },
  },

  // Sign In Page
  signIn: {
    title: 'Welcome back',
    subtitle: 'Sign in to your email/password authentication',
    card: {
      title: 'Organization Sign In',
      description: 'Enter your Integration Owner email to access your JWT authentication dashboard',
    },
    form: {
      email: {
        label: 'Email',
        placeholder: 'owner@yourorganization.com',
      },
      password: {
        label: 'Password',
        placeholder: 'Enter your password',
      },
      signIn: 'Sign in',
      signingIn: 'Signing in...',
      noAccount: {
        text: "Don't have an organization?",
        link: 'Create one',
      },
    },
    errors: {
      emailRequired: 'Email is required',
      failed: 'Failed to sign in',
      unexpected: 'An unexpected error occurred. Please try again.',
    },
    success: {
      message: 'Successfully signed in!',
    },
    agreement: {
      text: 'By signing in, you agree to our',
      terms: 'Terms of Service',
      and: 'and',
      privacy: 'Privacy Policy',
    },
  },

  // Sign Up
  signUp: {
    title: 'Create your organization',
    subtitle: 'Start your organization with email/password authentication',
    card: {
      title: 'Create Organization',
      description: 'Set up your organization to get started with email/password authentication and JWT tokens',
    },
    form: {
      organizationName: {
        label: 'Organization Name',
        placeholder: 'Your Organization',
      },
      organizationWebsite: {
        label: 'Organization Website',
        placeholder: 'yourorganization.com',
      },
      emailOwner: {
        label: 'Owner Email',
        placeholder: 'owner@yourorganization.com',
      },
      password: {
        label: 'Password',
        placeholder: 'Create a strong password',
      },
      confirmPassword: {
        label: 'Confirm password',
        placeholder: 'Confirm your password',
      },
      agreeToTerms: 'I agree to the Terms of Service and Privacy Policy',
      acceptMarketing: 'Send me product updates and marketing emails',
      createOrganization: 'Create organization',
      creatingOrganization: 'Creating organization...',
      haveAccount: {
        text: 'Already have an account?',
        link: 'Sign in',
      },
    },
    errors: {
      organizationNameRequired: 'Organization name is required',
      organizationWebsiteRequired: 'Organization website is required',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      passwordsDoNotMatch: 'Passwords do not match',
      agreeToTermsRequired: 'You must agree to the Terms of Service and Privacy Policy',
      failed: 'Failed to create organization',
      unexpected: 'An unexpected error occurred. Please try again.',
    },
    success: {
      message: 'Organization created successfully!',
    },
    trial: {
      description: 'Start managing your organization\'s authentication today.',
      features: {
        emailPassword: 'Email/password authentication',
        jwtTokens: 'JWT token security',
        simpleLogin: 'Simple login',
      },
    },
  },

  // Pricing Page
  pricing: {
    title: 'AuthFlow™ Pricing - Simple Email/Password Authentication Costs',
    description: 'Simple, transparent pricing for email/password authentication with JWT tokens. Start free and scale as you grow. No hidden fees, reliable authentication pricing.',
    keywords: [
      'authentication pricing',
      'email password pricing',
      'JWT pricing',
      'simple authentication pricing',
      'developer authentication pricing',
      'reliable authentication costs',
      'secure login pricing',
      'token-based auth pricing',
      'free authentication trial'
    ],
    ogTitle: 'AuthFlow™ Pricing - Simple Email/Password Authentication Costs',
    ogDescription: 'Choose the perfect plan for your needs. Start free and scale as you grow with reliable email/password authentication and JWT tokens.',
    hero: {
      title: 'Simple, transparent',
      titleAccent: 'authentication pricing',
      description: 'Choose the perfect plan for your authentication needs. Start free and scale as you grow with email/password authentication via JWT session tokens. No hidden fees, no surprises.',
    },
    breadcrumb: {
      product: 'Product',
      pricing: 'Pricing',
    },
    plans: {
      starter: {
        name: 'Starter',
        description: 'Perfect for small projects and testing email/password authentication',
        price: '$0',
        period: '/month',
        features: {
          users: 'Up to 10 monthly active users',
          basicAuth: 'Basic email/password authentication',
          support: 'Standard support',
        },
        cta: 'Get Started Free',
      },
      pro: {
        name: 'Pro',
        description: 'For growing businesses needing reliable email/password authentication',
        price: '$9.99',
        period: 'first month, then $19.99/month',
        badge: 'Most Popular',
        features: {
          users: 'Unlimited monthly active users',
          advancedAuth: 'Advanced email/password authentication',
          support: 'Priority support',
        },
        cta: 'Start Free Trial',
        monthlyCta: 'Purchase Monthly',
        annualCta: 'Purchase Annual',
      },
    },
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know about our email/password authentication pricing and plans.',
      questions: {
        mau: {
          question: 'What counts as a monthly active user?',
          answer: 'A monthly active user (MAU) is a unique user who authenticates with your application at least once during a calendar month.',
        },
        changePlans: {
          question: 'Can I change plans anytime?',
          answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated.',
        },
        freeTrial: {
          question: 'Is there a free trial?',
          answer: 'Yes, all paid plans come with a 3-day free trial. After 3 days, upgrading is only required if your organization exceeds 10 monthly active users. No credit card required to start.',
        },
        paymentMethods: {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, and Google and Apple Pay.',
        },
      },
    },
    cta: {
      title: 'Ready to get started?',
      description: 'Join thousands of developers who trust AuthFlow™ for reliable email/password authentication. Start your free trial today.',
      startTrial: 'Start Free Trial',
      contactSales: 'Contact Sales',
    },
    footer: {
      description: 'Simple, reliable email/password authentication with JWT tokens. Trusted by developers worldwide.',
      product: {
        title: 'Product',
        features: 'Features',
        pricing: 'Pricing',
        apiReference: 'API Reference',
      },
      resources: {
        title: 'Resources',
        gettingStarted: 'Getting Started',
        apiReference: 'API Reference',
        support: 'Support',
      },
      company: {
        title: 'Company',
        about: 'About',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
      },
      copyright: '© 2024 AuthFlow™. All rights reserved.',
    },
  },

  // Get Started Page
  getStarted: {
    title: 'AuthFlow™ Get Started - Email/Password Authentication Setup Guide | JWT API Integration',
    description: 'Complete guide to implement email/password authentication with AuthFlow™. Set up JWT tokens for secure login in minutes. Developer-friendly API for reliable authentication.',
    keywords: [
      'authentication setup',
      'email password authentication',
      'JWT integration',
      'secure login guide',
      'developer authentication',
      'API authentication tutorial',
      'token-based authentication',
      'authentication API guide'
    ],
    ogTitle: 'AuthFlow™ Get Started - Email/Password Authentication Setup Guide | JWT API Integration',
    ogDescription: 'Complete guide to implement email/password authentication with AuthFlow™. Set up JWT tokens for secure login in minutes.',
    breadcrumb: {
      resources: 'Resources',
      getStarted: 'Get Started',
    },
    hero: {
      badge: 'Quick Setup Guide',
      title: 'Get started with',
      titleAccent: 'email/password authentication',
      description: 'Add secure email/password login to your app in minutes, using JWT token authentication provided by our platform. Follow our simple setup guide and you\'ll be up and authenticating users in no time.',
      createAccount: 'Create Free Account',
    },
    steps: {
      title: 'Three simple steps to secure email/password authentication',
      subtitle: 'Follow these steps to integrate AuthFlow™\'s email/password authentication endpoints into your backend applications.',
      step1: {
        number: '1',
        title: 'Create Your Account',
        description: 'Sign up for AuthFlow™ and create your first project. You\'ll get your server-side API key instantly for secure email/password authentication.',
        signUp: 'Sign Up Free',
        viewDemo: 'View Demo',
      },
      step2: {
        number: '2',
        title: 'Integrate Auth Endpoints',
        description: 'Use our /auth endpoints from your backend to handle user authentication. All endpoints require your organization\'s API key for secure email/password login.',
        features: {
          apiKeyRequired: 'API Key Required',
          apiKeyDescription: 'All /auth endpoints require your organization\'s API key in JWT tokens for secure email/password authentication',
          backendIntegration: 'Backend Integration',
          backendDescription: 'Call from your server-side code, never expose API keys to frontend',
          sessionManagement: 'Session Management',
          sessionDescription: 'Handle user sessions with JWT tokens containing org API key for reliable email/password authentication',
        },
      },
      step3: {
        number: '3',
        title: 'Add Authentication',
        description: 'Wrap your app with your provider and start using authentication components. It\'s that simple with reliable email/password authentication!',
        quickStart: 'Quick Start',
        fullDocumentation: 'Full Documentation',
      },
    },
    apiDocs: {
      title: '/auth API Endpoints Technical Guide for Email/Password Authentication',
      subtitle: 'Complete technical documentation for integrating AuthFlow™\'s email/password authentication endpoints into your backend applications.',
      apiKeySetup: {
        title: 'API Key Configuration',
        description: 'Your organization\'s API key is embedded in all JWT tokens for /auth endpoints. Store this securely in your backend environment variables for reliable email/password authentication.',
        envVars: '# Environment Variables',
        apiKeyVar: 'AUTHFLOW_API_KEY=af_your_api_key_here',
        baseUrlVar: 'AUTHFLOW_BASE_URL=https://api.authflow.net',
        securityNote: 'Security Note: Never expose your API key in frontend code or client-side JavaScript. All /auth endpoint calls must be made from your backend server for secure email/password authentication.',
      },
      endpoints: {
        foobar: {
          title: 'GET /auth/fooBar',
          description: 'Generate temporary anti-abuse tokens for authentication flows',
          params: 'Parameters:',
          identityParam: 'identity (string): User\'s email address',
          response: 'Response:',
          example: 'Node.js Example:',
        },
        signUp: {
          title: 'POST /auth/signUp',
          description: 'Create new user accounts within your organization',
          params: 'Parameters:',
          foobarParam: 'foobar (string): Token from /auth/fooBar',
          apiKeyParam: 'api_key (string): Your organization\'s API key',
          emailParam: 'email (string): User\'s email address',
          passwordParam: 'password (string): User\'s password (8-32 chars, uppercase, lowercase, number, special char)',
          fullnameParam: 'fullname (string): User\'s full name. May include letters, digits, spaces, and standard punctuation.',
          disclaimedParam: 'disclaimed (string): Must be "true"',
          response: 'Response:',
          example: 'Node.js Example:',
        },
        login: {
          title: 'POST /auth/login',
          description: 'Authenticate users and receive session tokens',
          params: 'Parameters:',
          foobarParam: 'foobar (string): Token from /auth/fooBar',
          emailParam: 'email (string): User\'s email address',
          passwordParam: 'password (string): User\'s password',
          apiKeyParam: 'api_key (string): Your organization\'s API key',
          response: 'Response:',
          tokenNote: 'The returned JWT contains the authenticated user context.',
          example: 'Node.js Example:',
        },
        validate: {
          title: 'GET /auth/validate',
          description: 'Validate user session tokens and check authentication status',
          params: 'Parameters:',
          sessionTokenParam: 'sessionToken (string): JWT from /auth/login',
          emailParam: 'email (string): User\'s email address',
          response: 'Response:',
          example: 'Node.js Example:',
        },
        changePassword: {
          title: 'POST /auth/changePassword',
          description: 'Allow authenticated users to change their passwords',
          params: 'Parameters:',
          sessionTokenParam: 'sessionToken (string): JWT from /auth/login',
          emailParam: 'email (string): User\'s email address',
          newPasswordParam: 'newPassword (string): New password (same complexity requirements)',
          response: 'Response:',
          example: 'Node.js Example:',
        },
        logout: {
          title: 'GET /auth/logout',
          description: 'End user sessions securely',
          params: 'Parameters:',
          sessionTokenParam: 'sessionToken (string): JWT from /auth/login',
          emailParam: 'email (string): User\'s email address',
          response: 'Response:',
          example: 'Node.js Example:',
        },
      },
      implementation: {
        title: 'Complete Backend Integration Example',
        notes: 'Implementation Notes:',
        note1: 'Store session tokens securely (HTTP-only cookies recommended)',
        note2: 'Validate sessions on protected routes before processing requests',
        note3: 'Handle token expiration and refresh as needed',
        note4: 'Log authentication events for security monitoring',
      },
      errorHandling: {
        title: 'Error Handling & Security',
        statusCodes: 'Common HTTP Status Codes:',
        status200: '200: Success',
        status400: '400: Bad Request (missing/invalid parameters)',
        status401: '401: Unauthorized (invalid tokens, wrong API key, etc.)',
        status404: '404: User not found',
        status409: '409: Organization already exists (signup)',
        status500: '500: Server error',
        securityPractices: 'Security Best Practices:',
        practice1: 'Always validate JWT tokens before processing requests',
        practice2: 'Use HTTPS for all API calls',
        practice3: 'Implement rate limiting on authentication endpoints',
        practice4: 'Store session tokens in HTTP-only, secure cookies',
        practice5: 'Log all authentication attempts for security monitoring',
        practice6: 'Never log sensitive data like passwords or API keys',
        criticalNote: 'Critical: All /auth endpoints must be called from your backend server only. Never expose your API key or make these calls from client-side JavaScript, as this would compromise your organization\'s security.',
      },
    },
    features: {
      title: 'What you get outta-the-box with email/password authentication',
      subtitle: 'Wire the endpoints, we provide you the Sessions with reliable JWT tokens. It\'s that simple! 🤝',
    },
    cta: {
      title: 'Ready to add reliable email/password authentication?',
      description: 'Start your free trial today, have JWT authentication running by end of day.',
      createAccount: 'Create Free Account',
      talkToSales: 'Talk to Sales',
    },
    parameters: 'Parameters',
    response: 'Response',
    example: 'Example',
    runFailureScenario: 'Run as failure scenario',
    failureScenarioOn: 'Failure Scenario: On',
    running: 'Running...',
    runCurlConsole: 'Run cURL in Console',
    copied: 'Copied!',
    copyCurl: 'Copy curl',
    copyBackendExample: 'Copy backend code',
    required: 'required',
    optional: 'optional',
    consoleRunning: 'Console is running...',
    consoleDemoTitle: 'Linux Console Demo',
    consoleShellLabel: 'bash -- AuthFlow™ Console',
    notesTitle: 'Notes',
    endpointContent: {
      session: {
        fooBar: {
          summary: 'Generate a short-lived anti-abuse token before sign-up or login.',
          params: {
            identity: 'Email address for the auth user.',
            apiKey: 'Organization API key from the AuthFlow™ owner dashboard.',
            role: 'RBAC role to encode into the temporary token. Defaults to user.',
          },
          notes: {
            expires: 'This token expires in 45 seconds.',
            freshToken: 'Fetch a fresh fooBar anti-abuse token immediately before sign-up or login.',
          },
        },
        signUp: {
          summary: 'Create a new authentication user inside the owner organization.',
          params: {
            foobar: 'Temporary anti-abuse token returned by /auth/fooBar.',
            email: 'User email address.',
            password: 'Password matching {passwordRule}.',
            fullname: 'Full name for the auth user. May include letters, digits, spaces, and standard punctuation.',
            role: 'RBAC role for the user inside your application.',
            apiKey: 'Organization API key.',
            disclaimed: 'Boolean-like value stored with the user record. The collection uses true.',
          },
          notes: {
            successToken: 'Successful sign-up returns a session token immediately.',
            duplicateRole: 'If the same email and role already exist in your organization, the API returns a 409 payload.',
          },
        },
        login: {
          summary: 'Authenticate an auth user and return either a session token or a 2FA challenge.',
          params: {
            foobar: 'Temporary anti-abuse token returned by /auth/fooBar.',
            email: 'User email address.',
            password: 'User password.',
            apiKey: 'Organization API key.',
            role: 'User role for RBAC login.',
          },
          notes: {
            tempTokenInsteadOfToken: 'When 2FA is enabled, login returns tempToken instead of token.',
            exchangeThrough2fa: 'tempToken is then exchanged through one of the /auth/2fa verification endpoints.',
          },
        },
        validate: {
          summary: 'Validate a session token for an auth user.',
          params: {
            sessionToken: 'RS512 JWT session token.',
            email: 'User email address.',
          },
        },
        me: {
          summary: 'Return the current auth-user profile for a valid session.',
          params: {
            sessionToken: 'RS512 JWT session token.',
          },
        },
        logout: {
          summary: 'Log out the current auth user.',
          params: {
            sessionToken: 'RS512 JWT session token.',
            email: 'User email address.',
          },
        },
      },
      passwordManagement: {
        forgotPassword: {
          summary: 'Request a password-reset email for an auth user (enumeration-safe response).',
          params: {
            email: 'Auth-user email address.',
            apiKey: 'Organization API key.',
            role: 'Auth-user role. Defaults to user.',
          },
        },
        resetPassword: {
          summary: 'Reset an auth-user password with a valid reset token.',
          params: {
            token: 'Password-reset token from the email link.',
            newPassword: 'New password matching {passwordRule}.',
          },
        },
        changePassword: {
          summary: 'Change the authenticated auth-user password.',
          params: {
            sessionToken: 'Current RS512 JWT session token.',
            email: 'Email encoded in the session token.',
            oldPassword: 'Current password for confirmation.',
            newPassword: 'Replacement password matching {passwordRule}.',
          },
        },
      },
      emailVerification: {
        verifyEmail: {
          summary: 'Verify an auth-user email using the token delivered by AuthFlow™ email.',
          params: {
            token: 'Verification token from the email verification link.',
          },
          notes: {
            openedFromEmail: 'This endpoint is usually opened by clicking the verification link in email.',
            frontendHostedProxy: 'AuthFlow™ also supports frontend-hosted verification via /verify-email that proxies this check.',
          },
        },
        resendVerifyEmail: {
          summary: 'Resend the auth-user verification email when the account is not yet verified.',
          params: {
            foobar: 'Temporary token returned by /auth/fooBar.',
            email: 'Auth-user email address.',
            apiKey: 'Organization API key.',
            role: 'Auth-user role. Defaults to user.',
          },
        },
      },
      twoFactor: {
        enable: {
          summary: 'Generate a TOTP secret and QR code for an auth user.',
          params: {
            sessionToken: 'Current session token.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the session token already resolves the organization.',
            role: 'Auth-user role.',
          },
        },
        verifySetup: {
          summary: 'Verify the initial TOTP code and enable 2FA.',
          params: {
            sessionToken: 'Current session token.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the session token already resolves the organization.',
            role: 'Auth-user role.',
            totpCode: 'Six-digit code from the authenticator app.',
            secret: 'TOTP secret returned by /auth/2fa/enable.',
          },
        },
        disable: {
          summary: 'Disable TOTP 2FA for an auth user.',
          params: {
            sessionToken: 'Current session token.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the session token already resolves the organization.',
            role: 'Auth-user role.',
            password: 'Current password.',
          },
        },
        verify2fa: {
          summary: 'Exchange a login tempToken and TOTP code for a session token.',
          params: {
            tempToken: 'tempToken returned by /auth/login when 2FA is enabled.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the temp token already resolves the organization.',
            role: 'Auth-user role.',
            totpCode: 'Six-digit authenticator code.',
          },
        },
        backupEmail: {
          summary: 'Send a backup email OTP using a login tempToken.',
          params: {
            tempToken: 'tempToken returned by /auth/login.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the temp token already resolves the organization.',
            role: 'Auth-user role.',
          },
        },
        verifyEmailOtp: {
          summary: 'Exchange an emailed OTP for a session token.',
          params: {
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the temp token already resolves the organization.',
            role: 'Auth-user role.',
            emailOtp: 'Six-digit code delivered by email.',
          },
        },
        recoveryCodes: {
          summary: 'Regenerate recovery codes for an auth user.',
          params: {
            sessionToken: 'Current session token.',
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the session token already resolves the organization.',
            role: 'Auth-user role.',
            password: 'Current password.',
          },
        },
        verifyRecovery: {
          summary: 'Exchange a recovery code for a session token.',
          params: {
            email: 'Auth-user email.',
            apiKey: 'Organization API key. Optional when the temp token already resolves the organization.',
            role: 'Auth-user role.',
            recoveryCode: 'One of the 8-character recovery codes previously returned.',
          },
        },
      },
      environment: {
        baseUrl: 'Public API origin.',
        apiKey: 'Owner dashboard API key. Stored server-side only.',
        defaultRole: 'Role/RBAC value you assign to the auth user.',
      },
    },
    console: {
      promptPrefix: 'owner@authflow-demo:~$',
      launching: 'Launching AuthFlow™ Linux Console v1.0 ...',
      kernel: 'Kernel: Linux 6.8.0-35-generic x86_64',
      curlVersion: 'curl: 8.5.0, OpenSSL: 3.0.x',
      connected: 'Connected to api.authflow.net',
      sending: '> Sending {method} {path}',
      waiting: '> Waiting for response ...',
      serverHeader: '< server: api.authflow.net',
      processExited: '[process exited with code {code}]',
      status: {
        defaultSuccess: 'HTTP/1.1 200 OK (example)',
        signup: {
          success: 'HTTP/1.1 201 Created (example)',
          alt409: '# alt: HTTP/1.1 409 Conflict when email+role already exists',
        },
        login: {
          success: 'HTTP/1.1 200 OK (example)',
          alt2fa: '# alt: returns requires2FA/tempToken payload when 2FA is enabled',
        },
        verifyEmail: {
          success: 'HTTP/1.1 200 OK (example)',
          alt401: '# alt: HTTP/1.1 401 Unauthorized for invalid or expired verification token',
        },
        validate: {
          success: 'HTTP/1.1 200 OK (example)',
          alt401: '# alt: HTTP/1.1 401 Unauthorized for invalid or expired session token',
        },
        forgotPassword: {
          success: 'HTTP/1.1 200 OK (enumeration-safe response)',
        },
        changePassword: {
          success: 'HTTP/1.1 200 OK (example)',
          noJsonBody: '# no JSON body on success',
        },
      },
      failure: {
        signup: {
          status: 'HTTP/1.1 409 Conflict (example)',
          extra: '# failure scenario: duplicate email+role',
        },
        login: {
          status: 'HTTP/1.1 400 Bad Request (example)',
          extra: '# failure scenario: invalid credentials or role mismatch',
        },
        verifyEmail: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: stale verification token',
        },
        validate: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: expired session token',
        },
        me: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: missing or invalid sessionToken',
        },
        logout: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: invalid sessionToken',
        },
        changePassword: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: oldPassword invalid or session expired',
        },
        resendVerifyEmail: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: stale foobar token or invalid api_key',
        },
        fooBar: {
          status: 'HTTP/1.1 400 Bad Request (example)',
          extra: '# failure scenario: missing identity or api_key',
        },
        forgotPassword: {
          status: 'HTTP/1.1 400 Bad Request (example)',
          extra: '# failure scenario: missing email or api_key',
        },
        resetPassword: {
          status: 'HTTP/1.1 401 Unauthorized (example)',
          extra: '# failure scenario: stale reset token',
        },
        default: {
          status: 'HTTP/1.1 400 Bad Request (example)',
          extra: '# failure scenario',
        },
      },
    },
    pageContent: {
      badge: 'Auth Product Reference',
      title: 'AuthFlow™ API documentation',
      titleAccent: 'for Integration Owners',
      description: 'This Get Started documents our public auth API for your organization\'s users. AuthFlow™ provides 20 total /auth routes: 7 session routes for authentication and user management, 3 passwordManagement routes for password resets, 2 emailVerification routes for email verification, and 8 twoFactor routes for two-factor authentication. The downloadable Insomnia collection below contains all endpoint definitions, parameters, and example requests matching the exact API contracts.',
      downloadCollection: 'Download Insomnia Collection',
      createAccount: 'Create Owner Account',
      cloneStarterOnGitHub: 'Clone Next.js Starter',
      starterInfoLabel: 'Important security information about the Next.js starter',
      starterWarningTitle: 'Prototype and proof-of-concept use only',
      starterWarningPrototype: 'The authflow-nextjs-starter is intended only for prototypes and proof-of-concept frontends. It is not a production browser integration.',
      starterWarningSecret: 'Never put an AuthFlow API key in production frontend code, browser-visible environment variables, static assets, or client bundles. Anything shipped to a browser can be read by users and attackers.',
      starterWarningProduction: 'For production, send authentication requests through a trusted server-side backend or the AuthFlow ExpressJS proxy, and keep the API key in server-side environment variables or a managed secret store.',
      apiKeyWarningTitle: 'Never expose your AuthFlow API key in production browser code.',
      apiKeyWarningBody: 'API keys must stay on infrastructure you control. Do not embed them in JavaScript bundles, HTML, public environment variables, mobile web assets, logs, or network requests made directly by the browser. Use this ExpressJS proxy or your own backend so the key remains server-side.',
      integrationRulesTitle: 'Integration rules',
      integrationRulesRules: [
        'Integration Owners call AuthFlow™ from server-rendered code only. Your browser applications should store secrets as server-side environment variables, for calling the AuthFlow™ API. This is because: unlike the majority of authentication services, AuthFlow™ is designed to be used server-to-server, for general NIST-standards compliance.',
        'The Integration Owner (your) API key is required for core session-management and other routes.',
        'The fooBar token is a short-lived anti-abuse token and should be fetched immediately before each sign-up or login attempt, such that it is always fresh and valid.',
        'If login returns requires2FA--meaning the user has enabled their 2FA--then the user must complete one of the documented /auth/2fa verification exchanges to obtain a session token.',
      ],
      ownerAssetsTitle: 'Owner assets',
      ownerAssetsText: 'Use the exact collection your implementation team can import into Insomnia.',
      ownerAssetsCollectionName: 'AuthFlow_Consumers.yaml',
      collectionIncludes: 'The collection includes:',
      collectionItems: [
        '7 public /auth session routes',
        '3 public /auth passwordManagement routes',
        '2 public /auth emailVerification routes',
        '8 public /auth/2fa twoFactor routes',
        'Environment variables for API key, role/RBAC, session token, TOTP, temp token, and recovery code flows',
      ],
      checklistTitle: 'Implementation checklist',
      checklistSubtitle: 'This is the recommended Integration Owner workflow for wiring your organization to its authentication via AuthFlow™.',
      configStep: '1. Configure server-side secrets',
      implementStep: '2. Implement server-side auth',
      connectStep: '3. Connect your frontend',
      connectStepSecurityNote: 'Your browser code should never store AuthFlow™ API secrets, such as the owner API key.',
      implementStepRules: [
        'Fetch /auth/fooBar',
        'Call /auth/signUp or /auth/login',
        'Store session tokens securely',
        'Use /auth/validate, /auth/me, /auth/changePassword, and /auth/logout from protected routes',
      ],
      connectStepRules: [
        'Submit login and signup forms',
        'Handle 2FA prompts when the API reports requires2FA',
        'Store app sessions in secure cookies',
        'Load the current user through the session endpoints\' Responses',
      ],
      sessionEndpointsTitle: 'Session endpoints',
      sessionEndpointsSubtitle: 'These seven routes match authProduct.session from the backend root endpoint.',
      passwordMgmtTitle: 'Password management endpoints',
      passwordMgmtSubtitle: 'These three routes match authProduct.passwordManagement from the backend root endpoint.',
      emailVerificationTitle: 'Email verification endpoints',
      emailVerificationSubtitle: 'These two routes match authProduct.emailVerification from the backend root endpoint.',
      twoFactorTitle: 'Two-factor endpoints',
      twoFactorSubtitle: 'These eight routes match authProduct.twoFactor from the backend root endpoint.',
      serviceExampleTitle: 'Service example',
      securityRequirementsTitle: 'Security requirements',
      securityRequirements: [
        'Keep AUTHFLOW_API_KEY server-side and encrypted.',
        'Prefer HTTP-only secure cookies for your application session.',
        'Validate AuthFlow™ sessions before serving protected user data.',
        'Handle 400, 401, 404, 409, and 500 responses explicitly in your integration.',
      ],
      readyCtaTitle: 'Ready to wire your company authentication?',
      readyCtaDescription: 'Start with the downloadable collection, implement secure integration of the service with safely-stored secrets, and use the assistant on this page if you need guidance.',
      proxyTitlePrefix: 'ExpressJS ',
      proxyTitleSuffix: ' Proxy',
      proxyDescriptionPrefix: 'You may also choose to proxy the AuthFlow backend, from your own ExpressJS backend. This is arguably the most-secure way to go about consuming ',
      proxyDescriptionSuffix: ' in your application stacks.',
      proxyDetailsRoutes: 'The proxy forwards all AuthFlow /auth/* and /auth/2fa/* routes, keeps your organization API key server-side, and helps prevent secret exposure in browser code.',
      proxyDetailsDeploy: 'The proxy is Vercel-ready, supports local development immediately, and includes practical examples for sign-up, login, password flows, and 2FA verification.',
      proxyButton: 'View on GitHub',
    },
  },

  agent: {
    chatbot: {
      title: 'AuthFlow™ Assistant',
      inputPlaceholder: 'Ask about /auth or 2FA integration...',
      greeting: 'I guide Integration Owners through the exact AuthFlow™ auth-user contract. Ask me about owner API key setup, /auth endpoints, 2FA flows, the Insomnia collection, frontend-backend boundaries, or Settings-page account deletion.',
      downloadIntro: 'Use this direct download card to grab the owner collection your team can import immediately.',
      downloadTitle: 'Download Insomnia Collections',
      downloadButton: 'Download',
      noAnswer: 'I did not get a usable answer from the agent. Ask me about a specific endpoint, 2FA flow, or backend integration step and I will guide you through it.',
      error: 'I hit an error contacting the assistant. You can still ask about the exact /auth contract, the 2FA flows, or the downloadable Insomnia collection and try again.',
      toolNotFound: 'Tool not found',
    },
    tools: {
      verificationRecoveryDescription: 'Email verification and password recovery endpoints',
      accountSteps: ['1. Sign up as the Integration Owner at https://www.authflow.net/sign-up', '2. Create the organization in the owner dashboard', '3. Copy the organization API key from the dashboard', '4. Decide the role values your application will use for auth users', '5. Download the AuthFlow™ authentication-user Insomnia collection', '6. Give the collection and the exact backend contract to the team wiring your backend'],
      accountManagement: ['To permanently delete an organization, go to the AuthFlow™ dashboard Settings page.', 'Use the Delete Account section and type DELETE to confirm irreversible deletion.'],
      reminder: 'The owner API key belongs on the company backend only.',
      environmentNotes: ['Keep AUTHFLOW_API_KEY server-side only.', 'AUTHFLOW_DEFAULT_ROLE is optional, but useful if most users share a role.', 'Your frontend should call your own backend routes, not AuthFlow™ directly.'],
      backendGuidance: ['Fetch /auth/fooBar immediately before /auth/signUp or /auth/login.', 'Use the exact query parameter names from the public contract.', 'When /auth/login returns requires2FA, continue with the appropriate /auth/2fa route.', 'Validate and look up the current user from backend routes, then expose only your own session to the frontend.'],
      insomniaContains: ['All 12 public /auth endpoints (including verify-email, resend-verify-email, forgot-password, reset-password, deleteUser)', 'All 8 public /auth/2fa endpoints', 'Environment variables for API key, role, fooBar token, tempToken, session token, TOTP, email OTP, and recovery code flows'],
      troubleshoot: {
        solutions: {
          cors: 'If the browser is calling AuthFlow™ directly, stop there first. Your browser should call your backend, and your backend should call AuthFlow™.',
          '401_unauthorized': 'Check that api_key, role, and email match the exact values used when you fetched /auth/fooBar or created the user.',
          token_validation: 'Use the exact sessionToken and email pair for /auth/validate. If validation fails after login, confirm you are storing the returned AuthFlow™ session token, not the tempToken.',
          connection_failed: 'Confirm AUTHFLOW_BASE_URL is set to https://api.authflow.net and your backend can reach that host.',
          api_key_invalid: 'The owner API key must be the dashboard key that starts with af_. Do not confuse it with any frontend credential.',
          foobar_expired: 'fooBar expires quickly. Fetch it immediately before sign-up or login and do not reuse stale values.',
          verification_link: 'Ensure the verification token from the email link is valid and not expired. The token must be passed as a query parameter to /auth/verify-email.',
          password_reset: 'For password recovery, first call /auth/forgot-password with the user email and api_key. The user will receive a reset link with a token. Then call /auth/reset-password with that token and the new password.',
          two_factor_flow: 'If login returns requires2FA, use tempToken with /auth/2fa/verify-2fa or /auth/2fa/backup/email. Do not send tempToken to /auth/validate or /auth/me.',
          organization_deletion: 'Permanent organization deletion is managed in the AuthFlow™ dashboard Settings page. Go to Delete Account and type DELETE to confirm.',
          other: 'Compare your request against the exact public contract: parameter names, HTTP method, backend-only API key handling, and whether the route expects query params or JSON body.',
        },
        commonIssues: ['Frontend calling AuthFlow™ directly', 'Missing api_key or wrong role on /auth/fooBar or /auth/login', 'Using oldPassword/newPassword incorrectly on /auth/changePassword', 'Confusing tempToken with sessionToken', 'Expired or invalid verification tokens', 'Not calling /auth/forgot-password before /auth/reset-password', 'Skipping the 2FA verification exchange after requires2FA', 'Trying to delete an organization outside the Settings page flow'],
      },
    },
  },

  // Common
  comparisons: {
    hub: {
      breadcrumbRoot: 'Compare',
      breadcrumbCurrent: 'Auth Product Comparisons',
      title: 'Auth Product Comparisons',
      description: 'See how AuthFlow™ stacks up against the most popular authentication platforms. Side-by-side feature tables to help you choose the right auth solution for your project.',
      card: {
        firebase: {
          title: 'Firebase vs. Auth0 vs. AuthFlow™',
          description: 'Compare Google\'s Firebase Authentication with Auth0\'s enterprise platform and AuthFlow™\'s developer-first approach. See which authentication solution fits your stack.',
        },
        supabase: {
          title: 'Supabase vs. Auth0 vs. AuthFlow™',
          description: 'Compare Supabase\'s open-source auth with Auth0\'s enterprise offering and AuthFlow™\'s lean, focused authentication service. Ideal if you\'re already in the Supabase ecosystem.',
        },
      },
      bullets: {
        core: 'Core auth features & security',
        rbac: '2FA, RBAC, and multi-tenancy',
        supabaseModel: 'Open source vs. managed vs. focused',
        pricing: 'Pricing & vendor lock-in',
        dx: 'Developer experience & setup complexity',
      },
      viewComparison: 'View comparison',
    },
    labels: {
      fullySupported: 'Fully supported',
      partial: 'Partial / limited',
      notSupported: 'Not supported',
      feature: 'Feature',
      summary: 'Summary',
      startFree: 'Start free with AuthFlow™',
      alsoSee: 'Also see:',
      providers: {
        firebase: 'Firebase Auth',
        supabase: 'Supabase Auth',
        auth0: 'Auth0',
        authflow: 'AuthFlow™',
      },
      categories: {
        core: 'Core Authentication',
        security: 'Security',
        userOrg: 'User & Org Management',
        dx: 'Developer Experience',
        pricing: 'Pricing & Flexibility',
      },
    },
    features: {
      emailPassword: 'Email & Password Authentication',
      socialLogin: 'Social Login (Google, GitHub, etc.)',
      magicLinks: 'Magic Links / Passwordless',
      smsPhone: 'SMS / Phone Authentication',
      anonymous: 'Anonymous Authentication',
      jwt: 'JWT Token Management',
      rs512: 'RS512 Key-Pair Signing',
      twofa: 'Two-Factor Authentication (2FA)',
      totp: 'TOTP Authenticator App (Google Auth, Authy)',
      backupCodes: '2FA Backup Codes',
      emailOtp: '2FA via Email OTP',
      rateLimiting: 'Rate Limiting',
      emailVerification: 'Email Verification',
      passwordReset: 'Password Reset / Forgot Password',
      passwordComplexity: 'Password Complexity Enforcement',
      userDashboard: 'User Management Dashboard',
      rbac: 'Role-Based Access Control (RBAC)',
      multiTenancy: 'Multi-Tenancy / Organizations',
      perOrgApiKeys: 'Per-Org API Keys',
      auditLogs: 'Audit/Activity Logs',
      userBlocking: 'User Blocking / Suspension',
      simpleRest: 'Simple REST API (no SDK required)',
      officialSdks: 'Official Client SDKs',
      customEmailTemplates: 'Custom Email Templates',
      webhooks: 'Webhooks / Event Callbacks',
      selfHosted: 'Self-Hosted Option',
      openSource: 'Open Source',
      apiDocs: 'API Documentation',
      integratedDb: 'Integrated Database (PostgreSQL)',
      authOnly: 'Auth-Only (no database required)',
      freeTier: 'Free Tier Available',
      flatRate: 'Predictable Flat-Rate Pricing',
      payPerMau: 'Pay-Per-MAU Model',
      lowLockIn: 'Low Vendor Lock-In',
      lowSetup: 'Low Setup Complexity',
      noEcosystemBuyIn: 'Works Without Ecosystem Buy-In',
    },
    notes: {
      firebase2fa: 'Firebase requires upgrading to Identity Platform',
      firebaseRest: 'Firebase is SDK-first; bare REST requires manual token exchange',
      firebaseLockIn: 'Firebase heavily couples to the Google/Firebase ecosystem',
      supabaseRls: 'Supabase uses Row-Level Security (RLS) policies in Postgres',
      supabaseDb: 'Supabase Auth is tightly integrated with its Postgres database',
      supabaseLockIn: 'Supabase open source reduces lock-in, but its auth is coupled to its Postgres stack',
      supabaseAuthOnly: 'Supabase can be used for auth only but is designed as a full backend platform',
    },
    firebase: {
      breadcrumbCurrent: 'Firebase vs. Auth0 vs. AuthFlow™',
      title: 'Firebase vs. Auth0 vs. AuthFlow™',
      description: 'A side-by-side feature comparison of Google\'s Firebase Authentication, Okta\'s Auth0, and AuthFlow™ — so you can pick the right auth layer for your application.',
      summary: {
        firebaseTitle: 'Firebase Authentication',
        firebaseBody: 'Best for teams already invested in the Google/Firebase ecosystem. Generous free tier, SDK-first integration, but comes with ecosystem lock-in and requires Identity Platform for advanced features like 2FA.',
        auth0Title: 'Auth0',
        auth0Body: 'The most comprehensive feature set of the three — SAML, OIDC, enterprise SSO, extensive customization. Ideal for large teams with complex enterprise requirements. Pricing scales steeply with MAU.',
        authflowTitle: 'AuthFlow™',
        authflowBody: 'The right choice for teams that want email/password authentication with strong security (RS512 JWT, 2FA, RBAC) without buying into a large ecosystem. Simple REST API, predictable pricing, and low setup complexity.',
      },
      alsoSee: 'Supabase vs. Auth0 vs. AuthFlow™',
    },
    supabase: {
      breadcrumbCurrent: 'Supabase vs. Auth0 vs. AuthFlow™',
      title: 'Supabase vs. Auth0 vs. AuthFlow™',
      description: 'A side-by-side feature comparison of Supabase\'s open-source auth, Auth0\'s enterprise platform, and AuthFlow™ — so you can pick the right auth layer for your application.',
      summary: {
        supabaseTitle: 'Supabase Auth',
        supabaseBody: 'Best for teams that want an open-source, self-hostable backend. Supabase Auth is powerful and ships with social login, magic links, and deep Postgres integration. Ideal when you\'re already using Supabase as your database.',
        auth0Title: 'Auth0',
        auth0Body: 'The most comprehensive feature set — SAML, OIDC, enterprise SSO, and deep customization. The go-to for large teams with complex compliance requirements. Pricing escalates sharply with monthly active users.',
        authflowTitle: 'AuthFlow™',
        authflowBody: 'The right choice when you need focused, reliable email/password authentication with strong security (RS512 JWT, 2FA, RBAC) and no platform lock-in. Simple REST API, flat predictable pricing, and minimal setup.',
      },
      alsoSee: 'Firebase vs. Auth0 vs. AuthFlow™',
    },
  },

  // Common
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    language: 'Language',
  },

  // SEO
  seo: {
    title: 'AuthFlow™ - Secure Authentication Made Simple',
    description: 'Token-based authentication for web applications. Easy integration, enterprise security, and developer-friendly API. Start your free trial today.',
    keywords: ['authentication', 'auth', 'security', 'API', 'token-based auth', 'JWT', 'developer tools', 'web security', 'user management'],
  },

  // Footer (shared across pages)
  footer: {
    description: 'Simple, reliable email/password authentication with JWT tokens. Trusted by developers worldwide.',
    product: 'Product',
    resources: 'Resources',
    company: 'Company',
    apiReference: 'API Reference',
    about: 'About',
    support: 'Support',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    copyright: '© 2024 AuthFlow™. All rights reserved.',
  },
}
